import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Heading, Text, VStack } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import {
    getHttpStatus,
    useCompleteIdentityInvitationMutation,
    useGetIdentityInvitationQuery,
} from '../../api';
import { IdentityInvitation } from '../../api/kyc/types';
import Button from '../../components/base/Button';
import { CompanyLogoHeader } from '../../components/auth/CompanyLogoHeader';
import { useTranslation } from '../../hooks/useTranslation';

const IdentityVerificationPage: React.FC = () => {
    const { t, locale } = useTranslation({ setLangFromURL: true });
    const text = t.identityVerification;
    const location = useLocation();
    // Fragment не отправляется веб-серверу и не попадает в access log/referrer.
    const token = new URLSearchParams(location.hash.slice(1)).get('token') || '';
    const validToken = /^[A-Za-z0-9_-]{43}$/.test(token);
    const invitation = useGetIdentityInvitationQuery(token, {
        skip: !validToken,
        refetchOnMountOrArgChange: true,
    });
    const [complete, completion] = useCompleteIdentityInvitationMutation();
    const [consent, setConsent] = useState(false);
    const [result, setResult] = useState<{ token: string; info: IdentityInvitation } | null>(null);
    const { reset } = completion;

    useEffect(() => {
        setConsent(false);
        setResult(null);
        reset();
    }, [token, reset]);

    const info = result?.token === token ? result.info : invitation.currentData;
    const unavailable =
        !validToken || getHttpStatus(invitation.error) === 410 || getHttpStatus(completion.error) === 410;
    const succeeded = !unavailable && info?.status === 'verified' && info.mock;
    const submit = async () => {
        if (!consent || completion.isLoading || !validToken) return;
        try {
            const info = await complete({ token, consent: true }).unwrap();
            setResult({ token, info });
        } catch {
            // RTK Query сохраняет ошибку; автоматического повтора POST нет.
        }
    };

    return (
        <Box minH="100dvh" bg="gray.100" display="flex" justifyContent="center" alignItems="center" p={4}>
            <Box
                w="full"
                maxW="460px"
                bg="white"
                p={{ base: 5, md: 8 }}
                borderRadius="lg"
                borderWidth="1px"
                borderColor="gray.200"
            >
                <VStack align="stretch" gap={6}>
                    <CompanyLogoHeader />
                    <Heading size="lg" textAlign="center" color="gray.800">
                        {succeeded ? text.successTitle : text.title}
                    </Heading>
                    <Box bg="yellow.50" borderWidth="1px" borderColor="yellow.200" p={3}>
                        <Text fontSize="sm">{text.mockNotice}</Text>
                    </Box>
                    {unavailable ? (
                        <Box role="alert">
                            <Text fontWeight="semibold">{text.invalidTitle}</Text>
                            <Text mt={2}>{text.invalidDescription}</Text>
                        </Box>
                    ) : invitation.isLoading || invitation.isFetching ? (
                        <Text role="status">{text.loading}</Text>
                    ) : invitation.isError || !info?.mock ? (
                        <Box role="alert">
                            <Text>{text.error}</Text>
                            <Button mt={3} buttonType="secondary" onClick={() => void invitation.refetch()}>
                                {text.retry}
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <VStack align="stretch" gap={2} textAlign="center">
                                <Text fontWeight="semibold" fontSize="lg">
                                    {info.company_name}
                                </Text>
                                <Text color="gray.500">{text.roles[info.role] || info.role}</Text>
                                <Text fontWeight="bold" fontSize="xl">
                                    {info.full_name}
                                </Text>
                            </VStack>
                            {succeeded ? (
                                <Box bg="green.50" color="green.800" p={4} role="status">
                                    <Text>{text.successDescription}</Text>
                                </Box>
                            ) : (
                                <>
                                    <Text textAlign="center" color="gray.500" fontSize="sm">
                                        {text.expires.replace(
                                            '{date}',
                                            new Date(info.expires_at).toLocaleString(locale),
                                        )}
                                    </Text>
                                    <Checkbox.Root
                                        checked={consent}
                                        onCheckedChange={(details) => setConsent(details.checked === true)}
                                        disabled={completion.isLoading}
                                    >
                                        <Checkbox.HiddenInput />
                                        <Checkbox.Control
                                            borderRadius="0"
                                            borderWidth="2px"
                                            borderColor="gray.300"
                                            _checked={{ bg: 'yellow.500', borderColor: 'yellow.500' }}
                                        >
                                            <Checkbox.Indicator color="black" />
                                        </Checkbox.Control>
                                        <Checkbox.Label>{text.consent}</Checkbox.Label>
                                    </Checkbox.Root>
                                    {completion.isError && (
                                        <Text color="red.700" role="alert">
                                            {text.error}
                                        </Text>
                                    )}
                                    <Button
                                        buttonType="success"
                                        size="lg"
                                        w="full"
                                        disabled={!consent || completion.isLoading}
                                        isLoading={completion.isLoading}
                                        onClick={() => void submit()}
                                    >
                                        {text.continue}
                                    </Button>
                                </>
                            )}
                        </>
                    )}
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                        {text.help}
                    </Text>
                </VStack>
            </Box>
        </Box>
    );
};

export default IdentityVerificationPage;
